import { LLMProvider, LLMChatRequest, StreamCallbacks, LLMConfig, ToolSchema } from '../../types';

interface AnthropicMessage {
  role: 'user' | 'assistant';
  content:
    | string
    | Array<{
        type: 'text' | 'tool_use' | 'tool_result';
        text?: string;
        id?: string;
        name?: string;
        input?: Record<string, unknown>;
        tool_use_id?: string;
        content?: string;
      }>;
}

interface AnthropicStreamEvent {
  type: string;
  index?: number;
  delta?: {
    type: string;
    text?: string;
    partial_json?: string;
  };
  content_block?: {
    type: string;
    id?: string;
    name?: string;
    text?: string;
  };
}

const convertToAnthropicTools = (tools?: ToolSchema[]) => {
  if (!tools || tools.length === 0) return undefined;

  return tools.map((tool) => ({
    name: tool.name,
    description: tool.description,
    input_schema: tool.parameters,
  }));
};

const convertToAnthropicMessages = (request: LLMChatRequest): AnthropicMessage[] => {
  const messages: AnthropicMessage[] = [];

  for (const msg of request.messages) {
    if (msg.role === 'user') {
      messages.push({ role: 'user', content: msg.content });
    } else if (msg.role === 'assistant') {
      if (msg.toolCalls && msg.toolCalls.length > 0) {
        const content: AnthropicMessage['content'] = [];
        if (msg.content) {
          content.push({ type: 'text', text: msg.content });
        }
        for (const tc of msg.toolCalls) {
          content.push({
            type: 'tool_use',
            id: tc.id,
            name: tc.name,
            input: tc.arguments,
          });
        }
        messages.push({ role: 'assistant', content });
      } else {
        messages.push({ role: 'assistant', content: msg.content });
      }
    } else if (msg.role === 'tool' && msg.toolResult) {
      messages.push({
        role: 'user',
        content: [
          {
            type: 'tool_result',
            tool_use_id: msg.toolResult.toolCallId,
            content: JSON.stringify(msg.toolResult.result),
          },
        ],
      });
    }
  }

  return messages;
};

export const createAnthropicProvider = (config: LLMConfig): LLMProvider => {
  let abortController: AbortController | null = null;

  const chat = async (request: LLMChatRequest, callbacks: StreamCallbacks): Promise<void> => {
    abortController = new AbortController();

    try {
      const messages = convertToAnthropicMessages(request);
      const tools = convertToAnthropicTools(request.tools);

      const response = await fetch(`${config.baseUrl}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': config.apiKey || '',
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: config.model,
          system: request.systemPrompt,
          messages,
          tools,
          stream: true,
          max_tokens: config.maxTokens ?? 2048,
          temperature: config.temperature ?? 0.7,
        }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Anthropic request failed: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('No response body received');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';
      let currentToolUse: { id: string; name: string; input: string } | null = null;

      while (true) {
        const { done, value } = await reader.read(); // eslint-disable-line no-await-in-loop
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          try {
            const json: AnthropicStreamEvent = JSON.parse(trimmed.slice(6));

            if (json.type === 'content_block_start' && json.content_block?.type === 'tool_use') {
              currentToolUse = {
                id: json.content_block.id || '',
                name: json.content_block.name || '',
                input: '',
              };
            }

            if (json.type === 'content_block_delta') {
              if (json.delta?.type === 'text_delta' && json.delta.text) {
                fullContent += json.delta.text;
                callbacks.onToken(json.delta.text);
              }

              if (json.delta?.type === 'input_json_delta' && json.delta.partial_json && currentToolUse) {
                currentToolUse.input += json.delta.partial_json;
              }
            }

            if (json.type === 'content_block_stop' && currentToolUse) {
              let args: Record<string, unknown>;
              try {
                args = JSON.parse(currentToolUse.input);
              } catch {
                args = {};
              }

              callbacks.onToolCall({
                id: currentToolUse.id,
                name: currentToolUse.name,
                arguments: args,
              });
              currentToolUse = null;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      callbacks.onComplete(fullContent);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        return;
      }
      callbacks.onError(error as Error);
    } finally {
      abortController = null;
    }
  };

  const cancelRequest = (): void => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  return { chat, cancelRequest };
};
